import React, { useMemo, useEffect, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Category,
  Logarithmic,
  Tooltip,
  Inject,
  Legend,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

const DEFAULT_PALETTE = [
  "#eb4132",
  "#fcbd01",
  "#31aa52",
  "#1976d2",
  "#9c27b0",
  "#ff6f00",
  "#00acc1",
  "#6d4c41",
  "#d81b60",
  "#3949ab",
  "#7cb342",
  "#00897b",
];

const getCompareColor = (i) => DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];

function resolveColor(i, colors) {
  if (Array.isArray(colors) && colors.length > 0) {
    return colors[i % colors.length];
  }
  return getCompareColor(i);
}

export default function MultiLineAvgChart({
  title = "Average Active TV",
  series = [],
  colors,
  refreshKey,
  yAxisScale = "linear",
  yName = "active",
  logBase = 10,
}) {
  const chartRef = useRef(null);

  useEffect(() => {
    chartRef.current?.refresh();
  }, [refreshKey]);

  const safeSeries = useMemo(
    () => (Array.isArray(series) ? series : []),
    [series]
  );

  const useLog = yAxisScale === "log";

  // ✅ DATA FIX (log için 0 → 1)
  const plottedSeries = useMemo(() => {
    return safeSeries.map((s) => ({
      ...s,
      data: (s.data || []).map((p) => {
        const raw = Number(p?.active);
        const rawSafe = Number.isFinite(raw) ? Math.max(0, raw) : 0;

        if (useLog) {
          return {
            ...p,
            rawActive: rawSafe,
            activePlot: rawSafe <= 0 ? 1 : rawSafe,
          };
        }

        return {
          ...p,
          rawActive: rawSafe,
          active: rawSafe,
        };
      }),
    }));
  }, [safeSeries, useLog]);

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const axisTextColor = storedUser?.darkmode ? "#fff" : "#666";

  const handleLegendClick = () => {
    setTimeout(() => {
      chartRef.current?.refresh();
    }, 0);
  };

  const handleTooltipRender = (args) => {
    const raw = args?.point?.rawActive;

    if (raw !== undefined) {
      args.text = args.text.replace(/:\s*.*$/, `: ${formatNumber(raw)}`);
    }
  };

  // ✅ CLEAN LOG LABEL (SADECE 10^n GÖSTER)
  const handleAxisLabelRender = (args) => {
    if (args.axis?.name === "primaryYAxis") {
      const val = Number(args.value);

      if (!isNaN(val)) {
        if (useLog) {
          const isPower = Number.isInteger(val);

          if (isPower) {
            const realValue = Math.pow(logBase, val);
            args.text = formatNumber(realValue);
          } else {
            args.text = ""; // 🔥 ara tick'leri gizle
          }
        } else {
          args.text = formatNumber(val);
        }
      }
    }
  };

  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>

      <ChartComponent
        ref={chartRef}
        legendClick={handleLegendClick}
        primaryXAxis={{
          valueType: "Category",
          labelIntersectAction: "Rotate45",
          labelStyle: { fontSize: "11px", color: axisTextColor },
          majorGridLines: { width: 0 },
        }}
        primaryYAxis={{
          minimum: useLog ? 1 : 0,
          valueType: useLog ? "Logarithmic" : "Double",
          logBase: useLog ? logBase : undefined,
          interval: useLog ? 1 : undefined, // 🔥 SADECE 10^n
        }}
        legendSettings={{
          visible: true,
          position: "Top",
          alignment: "Center",
          toggleVisibility: true,
        }}
        tooltip={{ enable: true }}
        tooltipRender={handleTooltipRender}
        axisLabelRender={handleAxisLabelRender}
        height="420px"
      >
        <Inject
          services={[LineSeries, Category, Logarithmic, Tooltip, Legend]}
        />

        <SeriesCollectionDirective>
          {plottedSeries.map((s, i) => {
            const color = resolveColor(i, colors);

            return (
              <SeriesDirective
                key={`${s.name}-${i}`}
                dataSource={s.data || []}
                xName="x"
                yName={useLog ? "activePlot" : yName}
                type="Line"
                name={s.name}
                width={3}
                fill={color}
                border={{ color, width: 3 }}
                animation={{ enable: true, duration: 500 }}
              />
            );
          })}
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
}